import { useStore } from '@nanostores/react';
import { useEffect, useState } from 'react';
import { $connectionTimeout, $showTimeoutBanner, clearTimeoutState } from '~/lib/stores/connection-status';

function formatTimeRemaining(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }

  return `${seconds}s`;
}

export function ConnectionTimeoutBanner() {
  const state = useStore($connectionTimeout);
  const showBanner = useStore($showTimeoutBanner);
  const [displayTime, setDisplayTime] = useState(state.remainingMs);

  useEffect(() => {
    if (!state.isWarning || !state.disconnectAt) {
      return undefined;
    }

    const targetTime = new Date(state.disconnectAt).getTime();

    const interval = setInterval(() => {
      const remaining = Math.max(0, targetTime - Date.now());
      setDisplayTime(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [state.isWarning, state.disconnectAt]);

  if (!showBanner) {
    return null;
  }

  if (state.isTimedOut) {
    return (
      <div className="ui-banner-error">
        <span className="i-ph:warning text-danger text-[14px] flex-shrink-0" />
        <span className="flex-1 min-w-0">Session ended: {state.timeoutReason}</span>
        <button type="button" onClick={clearTimeoutState} aria-label="Dismiss" className="ui-btn-icon">
          <span className="i-ph:x text-[12px]" />
        </button>
      </div>
    );
  }

  const isUrgent = displayTime <= 5 * 60 * 1000;

  return (
    <div className={isUrgent ? 'ui-banner-error' : 'ui-banner-warning'}>
      <span className={`i-ph:clock text-[14px] flex-shrink-0 ${isUrgent ? 'text-danger' : 'text-warning'}`} />
      <span className="flex-1 min-w-0">Session ending in {formatTimeRemaining(displayTime)} — save your work</span>
      <button type="button" onClick={clearTimeoutState} aria-label="Dismiss" className="ui-btn-icon">
        <span className="i-ph:x text-[12px]" />
      </button>
    </div>
  );
}
